import React, { useEffect, useRef, useState } from "react";
import BackHeader from "@components/header/BackHeader";
import { FormProvider, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { productSchema } from "@libs/zod/zodVaildation";
import { useApiRequest } from "@utils/ApiRequest";
import { useMutation, useQuery } from "@tanstack/react-query";
import BasicButton from "@components/button/BasicButton";
import {
  BrandCheckList,
  ColorWithSize,
  PriceInput,
  ProductDescription,
  ProductNameInput,
  ProductInformationInput,
  ProductPhotos,
  ProductClassificationFields,
  ShippingSpecInput,
} from "@pages/register/_components/index";
import LabelWithInput from "@components/input/LabelWithInput";
import CheckBoxInput from "@components/input/CheckBoxInput";
import focusFirstError from "@utils/firstErrorFocus";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import useModalStore from "@store/modalStore";
import { queryClient } from "@libs/reactQuery/queryClient";
import ScrollToTop from "react-scroll-to-top";
import {
  buildClassificationPayload,
  createOptionKey,
} from "@utils/productClassification";

import {
  buildProductInformationPayload,
  productInformationDefaults,
  productInformationSchema,
  productInformationTextFields,
} from "@utils/productInformation";

const RegisterProduct: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getUserInfo,
    postProduct,
    getProductDetail,
    getAiTrainingData,
    getColorList,
    postProductTemp,
  } = useApiRequest();
  const { modals, openModal, closeModal } = useModalStore();
  const { id, status } = useParams();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  // 등록 모드 구분 (2가지만)
  const isEditMode = !!(id && status);
  const isNewMode = !isEditMode; // 새로 등록 (AI 데이터 포함)
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState("");

  // 2. Query
  const { data: productInfo } = useQuery({
    queryKey: ["productInfo", id, status],
    queryFn: () => getProductDetail(Number(id), status),
    enabled: isEditMode,
  });

  const { data: aiTrainingData } = useQuery({
    queryKey: ["aiTrainingData"],
    queryFn: () => getAiTrainingData(Number(id)),
    enabled: isNewMode,
  });

  const { data: userInfo, isLoading: userInfoLoading } = useQuery({
    queryKey: ["userInfo"],
    queryFn: getUserInfo,
  });

  const { data: colorList } = useQuery({
    queryKey: ["colorList"],
    queryFn: getColorList,
  });

  const { mutate } = useMutation({
    mutationFn: (data: any) => postProduct(data),
    onSuccess: (data) => {
      const { product_id, status } = data;
      navigate(`/register-success/${Number(product_id)}/${status}`, {
        replace: true,
      });
      queryClient.invalidateQueries({
        queryKey: ["productInfo"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getProductInventorySimple"],
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.data?.message ||
        "상품 등록에 실패했습니다.";
      setErrorMessage(message);
      openModal("errorModal");
    },
  });

  const { mutate: postProductTempMutate } = useMutation({
    mutationFn: (data: any) => postProductTemp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["productInfo"],
      });
      navigate(returnTo || "/capture-list", { replace: true });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.data?.message ||
        "임시 저장에 실패했습니다.";
      setErrorMessage(message);
      openModal("errorModal");
    },
  });

  // 폼 초기화 및 설정
  const method = useForm({
    resolver: zodResolver(productSchema()),
    mode: "onChange",
    reValidateMode: "onChange",

    defaultValues: {
      _translation_status: "idle",
      ...productInformationDefaults(),
      product_id: productInfo?.product_id || null,
      brand_id: null,
      brand_name: "",
      size_type_id: "",
      name_ko: "",
      name_en: "",
      depth1: null,
      primary_color_id: null,
      pattern: "",
      stock_list: [] as any,
      image_list: [] as any,
      thumbnail_url: "",
      tag_type_ids: [] as number[],
      tag_style_ids: [] as number[],
      tag_fit_ids: [] as number[],
      tag_occasion_ids: [] as number[],
      tag_color_pattern_ids: [] as number[],
      audience_category_ids: [] as number[],
      patterns: [] as any[],
      product_weight_kg: null,
      shipping_weight_kg: null,
      volume_width_cm: null,
      volume_depth_cm: null,
      volume_height_cm: null,
      price: 0,
      description_ko: "",
      description_en: "",
      terms: false,
    },
  });

  const { handleSubmit, watch, reset, getValues, setValue } = method;

  // 뒤로가기 처리
  const handleBackEvent = (): void => {
    openModal("backModal");
  };

  // 5. useEffect

  // 임시 저장 데이터 상품등록 할 때
  useEffect(() => {
    if (productInfo && isEditMode) {
      reset({
        ...productInformationDefaults(productInfo),
        product_id: productInfo.product_id || null,
        size_type_id: productInfo.size_type_id || "",
        brand_id: productInfo.brand_id ?? null,
        brand_name: productInfo.brand_name || productInfo.brand_name_ko || "",
        name_ko: productInfo.product_name_ko || productInfo.name_ko || "",
        name_en: productInfo.product_name_en || productInfo.name_en || "",
        depth1: productInfo.category_depth1_id || productInfo.category_id || null,
        primary_color_id: productInfo.primary_color_id ?? null,
        pattern: productInfo.pattern || "",
        description_ko: productInfo.description_ko || "",
        description_en: productInfo.description_en || "",
        tag_type_ids: productInfo.tag_type_ids || [],
        tag_style_ids: productInfo.tag_style_ids || [],
        tag_fit_ids: productInfo.tag_fit_ids || [],
        tag_occasion_ids: productInfo.tag_occasion_ids || [],
        tag_color_pattern_ids: productInfo.tag_color_pattern_ids || [],
        audience_category_ids:
          productInfo.classification?.audience_categories?.map((item: any) => Number(item.id)) ||
          [productInfo.category_depth1_id || productInfo.category_id].filter(Boolean),
        patterns:
          productInfo.classification?.patterns?.map((item: any) => ({
            tag_id: item.tag_id,
            name_ko: item.name_ko,
            name_en: item.name_en,
            name_ja: item.name_ja,
          })) || (productInfo.pattern ? [{ tag_id: null, name_ko: productInfo.pattern }] : []),
        product_weight_kg: productInfo.product_weight_kg ?? null,
        shipping_weight_kg: productInfo.shipping_weight_kg ?? null,
        volume_width_cm: productInfo.volume_width_cm ?? null,
        volume_depth_cm: productInfo.volume_depth_cm ?? null,
        volume_height_cm: productInfo.volume_height_cm ?? null,
        price: productInfo.price || 0,

        stock_list:
          productInfo.color_list?.map((color: any) => ({
            color_id: color.base_color_id ? null : color.color_id,
            option_key: color.option_key || createOptionKey(),
            color_name: color.name,
            color_name_ko: color.name,
            color_name_en: color.name_en,
            color_name_ja: color.name_ja,
            color_sku: color.color_sku || "",
            base_color_id: color.base_color_id,
            hex_code: color.hex_code,
            size_list:
              color.size_list?.map((size: any) => ({
                size_id: size.id,
                size_name: size.name,
                size_sku: size.size_sku || "",
                amount: size.amount,
              })) || [],
          })) || [],

        image_list:
          productInfo.product_image_list?.map(
            (item: any) => item.image_url,
          ) || [],
        thumbnail_url:
          productInfo.thumbnail_url ||
          productInfo.product_image_list?.[0]?.image_url ||
          "",
      });
    }
  }, [productInfo, method, reset, isEditMode]);

  // 새로 등록할 때
  useEffect(() => {
    if (aiTrainingData && isNewMode) {
      const matchedColor = colorList?.find(
        (c: any) => Number(c.id) === Number(aiTrainingData.color_id),
      );

      setValue("size_type_id", aiTrainingData.size_type_id || "");
      setValue(
        "depth1",
        aiTrainingData.category_depth1_id || aiTrainingData.category_id || null,
      );
      setValue("primary_color_id", aiTrainingData.color_id || null);
      setValue("pattern", aiTrainingData.pattern || "");
      setValue("tag_type_ids", aiTrainingData.tag_type || []);
      setValue("tag_style_ids", aiTrainingData.tag_style || []);
      setValue("tag_fit_ids", aiTrainingData.tag_fit || []);
      setValue("tag_occasion_ids", aiTrainingData.tag_occasion || []);
      setValue("tag_color_pattern_ids", []);
      setValue(
        "audience_category_ids",
        aiTrainingData.classification?.audience_category_ids || [aiTrainingData.category_depth1_id || aiTrainingData.category_id].filter(Boolean),
      );
      setValue(
        "patterns",
        aiTrainingData.classification?.patterns || (aiTrainingData.pattern ? [{ tag_id: null, name_ko: aiTrainingData.pattern }] : []),
      );
      setValue(
        "image_list",
        aiTrainingData.image_url ? [aiTrainingData.image_url] : [],
      );
      setValue("thumbnail_url", aiTrainingData.image_url || "");
      setValue("stock_list", [
        {
          ...(aiTrainingData.color_input || {}),
          color_id: aiTrainingData.color_input?.color_id ?? aiTrainingData.color_id,
          option_key: createOptionKey(),
          color_name: aiTrainingData.color_input?.color_name_ko || aiTrainingData.color_input?.color_name_en || matchedColor?.name,
          hex_code: aiTrainingData.color_input?.color_hex_code || matchedColor?.hex_code,
          size_list: [],
        },
      ]);
    }
  }, [aiTrainingData, reset, setValue, colorList, isNewMode]);

  // 폼 제출 처리
  const onSubmit = async (data: any) => {
    if (["pending", "loading"].includes(data._translation_status)) { setErrorMessage("일본어 번역이 끝난 후 저장해주세요."); openModal("errorModal"); return; }
    if (!userInfo?.id) {
      setErrorMessage("사용자 정보를 불러오지 못했습니다.");
      openModal("errorModal");
      return;
    }

    const categoryId =
      data.depth1 || productInfo?.category_depth1_id || productInfo?.category_id ||
      aiTrainingData?.category_depth1_id || aiTrainingData?.category_id;

    // 색상별 사이즈 목록을 평탄화 (백엔드 DTO: {color_id, size_id, amount}[])
    const postStockList = data.stock_list.flatMap((stock: any) =>
      (stock.size_list || []).map((size: any) => ({
        color_id: stock.color_id,
        option_key: stock.option_key || createOptionKey(),
        color_name_ko: stock.color_name_ko || stock.color_name || null,
        color_name_en: stock.color_name_en || null,
        color_name_ja: stock.color_name_ja || null,
        color_sku: stock.color_sku ?? "",
        size_sku: size.size_sku ?? "",
        color_hex_code: stock.hex_code || null,
        base_color_id: stock.base_color_id || null,
        size_id: size.size_id,
        amount: size.amount,
      })),
    );

    if (data.image_list.length > 0 && !data.thumbnail_url) {
      data.thumbnail_url = data.image_list[0];
    }

    const typeTagIds = (data.tag_type_ids || []).map(Number).filter(Boolean);
    const tagIds = [
      ...(data.tag_type_ids || []),
      ...(data.tag_style_ids || []),
      ...(data.tag_fit_ids || []),
      ...(data.tag_occasion_ids || []),
    ]
      .map(Number)
      .filter(Boolean);

    const primaryOptionKey = data.stock_list?.[0]?.option_key || postStockList?.[0]?.option_key || null;
    const classification = buildClassificationPayload({
      audienceCategoryIds: (data.audience_category_ids || []).map(Number),
      primaryColorOptionKey: primaryOptionKey,
      patterns: data.patterns || [],
    });

    const postData = {
      ...buildProductInformationPayload(data, isEditMode ? {
        original: productInfo || {},
        changedFields: method.formState.dirtyFields,
      } : undefined),
      product_id: isEditMode ? Number(productInfo?.product_id || id) : null,
      user_id: userInfo.id,
      brand_id: data.brand_id && data.brand_id > 0 ? data.brand_id : null,
      brand_name: data.brand_name || null,
      category_id: categoryId,
      type_tag_ids: typeTagIds,
      tag_ids: Array.from(new Set(tagIds)),
      tag_type_ids: typeTagIds,
      tag_style_ids: (data.tag_style_ids || []).map(Number).filter(Boolean),
      tag_fit_ids: (data.tag_fit_ids || []).map(Number).filter(Boolean),
      tag_occasion_ids: (data.tag_occasion_ids || []).map(Number).filter(Boolean),
      name_ko: data.name_ko,
      name_en: data.name_en,
      thumbnail_url: data.thumbnail_url,
      classification,
      price: data.price,
      product_weight_kg: data.product_weight_kg,
      shipping_weight_kg: data.shipping_weight_kg,
      volume_width_cm: data.volume_width_cm,
      volume_depth_cm: data.volume_depth_cm,
      volume_height_cm: data.volume_height_cm,
      description_ko: data.description_ko,
      description_en: data.description_en,
      image_list: data.image_list,
      stock_list: postStockList,
    };

    mutate(postData);
  };

  const watchTerms = watch("terms");
  const nameRef = useRef<HTMLDivElement | null>(null);
  const stockListRef = useRef<HTMLDivElement | null>(null);
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const brandRef = useRef<HTMLDivElement | null>(null);
  const imageListRef = useRef<HTMLDivElement | null>(null);
  const shippingSpecRef = useRef<HTMLDivElement | null>(null);
  const priceRef = useRef<HTMLDivElement | null>(null);
  const informationRef = useRef<HTMLDivElement | null>(null);

  const onError = (errors: any) => {
    // console.log("onError", errors);

    focusFirstError(errors, [
      { name: "name_ko", ref: nameRef },
      { name: "name_en", ref: nameRef },
      { name: "brand_id", ref: brandRef },
      { name: "description_ko", ref: descriptionRef },
      { name: "image_list", ref: imageListRef },
      { name: "stock_list", ref: stockListRef },
      { name: "product_weight_kg", ref: shippingSpecRef },
      { name: "shipping_weight_kg", ref: shippingSpecRef },
      { name: "volume_width_cm", ref: shippingSpecRef },
      { name: "volume_depth_cm", ref: shippingSpecRef },
      { name: "volume_height_cm", ref: shippingSpecRef },
      { name: "price", ref: priceRef },
      { name: "product_moq", ref: informationRef },
      ...productInformationTextFields.map(([name]) => ({ name, ref: informationRef })),
      { name: "terms", ref: null },
    ]);
  };

  // console.log("aiTrainingData", aiTrainingData);

  const handleTempSave = () => {
    if (!userInfo?.id) {
      setErrorMessage("사용자 정보를 불러오지 못했습니다.");
      openModal("errorModal");
      return;
    }

    const data = getValues();
    if (["pending", "loading"].includes(data._translation_status)) { setErrorMessage("일본어 번역이 끝난 후 저장해주세요."); openModal("errorModal"); return; }
    const information = productInformationSchema.safeParse(data);
    if (!information.success) {
      const issue = information.error.issues[0];
      method.setError(issue.path[0] as keyof typeof data, { type: "validate", message: issue.message });
      informationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const categoryId =
      data.depth1 || productInfo?.category_depth1_id || productInfo?.category_id ||
      aiTrainingData?.category_depth1_id || aiTrainingData?.category_id;

    const postStockList = data.stock_list.flatMap((stock: any) =>
      (stock.size_list || []).map((size: any) => ({
        color_id: stock.color_id,
        option_key: stock.option_key || createOptionKey(),
        color_name_ko: stock.color_name_ko || stock.color_name || null,
        color_name_en: stock.color_name_en || null,
        color_name_ja: stock.color_name_ja || null,
        color_sku: stock.color_sku ?? "",
        size_sku: size.size_sku ?? "",
        color_hex_code: stock.hex_code || null,
        base_color_id: stock.base_color_id || null,
        size_id: size.size_id,
        amount: size.amount,
      })),
    );

    const tempPostData = {
      ...buildProductInformationPayload(information.data, isEditMode ? {
        original: productInfo || {},
        changedFields: method.formState.dirtyFields,
      } : undefined),
      product_id: isEditMode ? Number(productInfo?.product_id || id) : null,
      user_id: userInfo.id,
      brand_id: data.brand_id && data.brand_id > 0 ? data.brand_id : null,
      brand_name: data.brand_name || null,
      category_id: categoryId,
      type_tag_ids: (data.tag_type_ids || []).map(Number).filter(Boolean),
      tag_ids: Array.from(
        new Set([
          ...(data.tag_type_ids || []),
          ...(data.tag_style_ids || []),
          ...(data.tag_fit_ids || []),
          ...(data.tag_occasion_ids || []),
        ].map(Number).filter(Boolean)),
      ),
      tag_type_ids: (data.tag_type_ids || []).map(Number).filter(Boolean),
      tag_style_ids: (data.tag_style_ids || []).map(Number).filter(Boolean),
      tag_fit_ids: (data.tag_fit_ids || []).map(Number).filter(Boolean),
      tag_occasion_ids: (data.tag_occasion_ids || []).map(Number).filter(Boolean),
      name_ko: data.name_ko || "",
      name_en: data.name_en || "",
      thumbnail_url: data.thumbnail_url,
      classification: buildClassificationPayload({
        audienceCategoryIds: (data.audience_category_ids || []).map(Number),
        primaryColorOptionKey: data.stock_list?.[0]?.option_key || postStockList?.[0]?.option_key || null,
        patterns: data.patterns || [],
      }),
      price: data.price || 0,
      product_weight_kg: data.product_weight_kg,
      shipping_weight_kg: data.shipping_weight_kg,
      volume_width_cm: data.volume_width_cm,
      volume_depth_cm: data.volume_depth_cm,
      volume_height_cm: data.volume_height_cm,
      description_ko: data.description_ko || null,
      description_en: data.description_en || null,
      image_list: data.image_list || [],
      stock_list: postStockList,
    };

    postProductTempMutate(tempPostData);
  };

  return userInfoLoading ? (
    <></>
  ) : (
    <FormProvider {...method}>
      <FormContainer>
        <BackHeader title="상품등록" onClick={handleBackEvent}></BackHeader>

        <ContentWrapper>
          <Container>
            <ProductNameInput nameRef={nameRef} />
            {/* <FadeInContainer isShow={!!watchDepth1}> */}

            {/* </FadeInContainer> */}

            <BrandCheckList brandRef={brandRef} />

            <ProductDescription descriptionRef={descriptionRef} />

            <ProductPhotos imageListRef={imageListRef} />

            <ProductClassificationFields />

            <ColorWithSize stockListRef={stockListRef} />

            <ProductInformationInput informationRef={informationRef} />

            <ShippingSpecInput specRef={shippingSpecRef} />

            <PriceInput priceRef={priceRef} />

            <LabelWithInput label="샤카라카 이용약관 동의" required>
              {method.formState.errors?.terms && (
                <TermsErrorMessage>
                  {method.formState.errors.terms.message?.toString()}
                </TermsErrorMessage>
              )}
              <CheckBoxInput
                label="샤카라카 이용약관 동의"
                checked={watchTerms}
                onClick={() => setValue("terms", !watchTerms)}
              />
            </LabelWithInput>

            {/* 제출 버튼 */}
            <BasicButton
              title="샤카라카에 상품 등록"
              height="5.2rem"
              width="100%"
              padding="1.6rem 1.2rem"
              onClick={handleSubmit(
                () => openModal("registerModal"), // 유효성 검사 통과 시 모달 열기
                onError, // 실패 시 포커스 이동
              )}
            />

            <BasicModal
              open={modals.registerModal}
              activeName="확인"
              activeEvent={() => {
                handleSubmit(onSubmit, onError)();
                closeModal("registerModal");
              }}
              closeName="취소"
              closeModal={() => closeModal("registerModal")}>
              <ModalContent
                mainMessage="작성하신 내용으로 상품을"
                subMessage="샤카라카에 판매 등록하시겠습니까?"
              />
            </BasicModal>

            {/* 뒤로가기 모달 */}
            <BasicModal
              open={modals.backModal}
              activeName="확인"
              activeEvent={() => {
                closeModal("backModal");
                handleTempSave();
              }}
              closeName="취소"
              closeModal={() => closeModal("backModal")}>
              <ModalContent
                mainMessage="이 상품을 샤카라카에 등록하지 않고"
                subMessage="돌아가시겠습니까?"
                errorMessage="샤카라카에 등록되지 않은 상품은 임시저장됩니다."
              />
            </BasicModal>

            <BasicModal
              open={modals.errorModal}
              activeName="확인"
              activeEvent={() => closeModal("errorModal")}
              closeModal={() => closeModal("errorModal")}>
              <ModalContent
                mainMessage="등록 중 오류가 발생했습니다"
                subMessage={errorMessage}
              />
            </BasicModal>
          </Container>
        </ContentWrapper>

        <ScrollToTop
          smooth
          component={
            <img
              src={theme.image.to_top.default}
              alt="to_top"
              onClick={(e: any) => e.preventDefault()}
            />
          }
          style={{
            position: "absolute",
            zIndex: 100,
            marginBottom: 60,
            right: 20,
            backgroundColor: "transparent",
            boxShadow: "none",
          }}
        />
      </FormContainer>
    </FormProvider>
  );
};

export default RegisterProduct;

const FormContainer = styled.form({
  position: "relative",
  display: "flex",
  flexDirection: "column",
});

const ContentWrapper = styled.div(({ theme }) => ({
  padding: "2rem 2rem 1rem 2rem",
}));

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "3rem",
});

const TermsErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
}));
